/**
 * PageSpeed Service
 * Provides quality scoring for generated sites
 * Uses Google PageSpeed Insights API (free tier) for deployed sites
 */

// Google PageSpeed Insights API (free, no key required for basic usage)
const PAGESPEED_API = 'https://www.googleapis.com/pagespeedonline/v5/runPagespeed'

/**
 * Calculate quality score from local HTML files
 * Estimates performance based on HTML best practices
 * @param {object} files - { filename: content } map
 * @returns {object} Quality score and metrics
 */
export function calculateQualityScore(files) {
  const fileList = Object.entries(files)
  if (fileList.length === 0) return null

  let totalScore = 0
  const metrics = {
    seo: { score: 0, checks: [] },
    performance: { score: 0, checks: [] },
    accessibility: { score: 0, checks: [] },
    bestPractices: { score: 0, checks: [] },
  }

  fileList.forEach(([filename, content]) => {
    if (!filename.endsWith('.html')) return

    // SEO checks
    const seoChecks = [
      { name: 'Meta title', pass: /<title>.+<\/title>/i.test(content) },
      { name: 'Meta description', pass: /<meta[^>]*name=["']description["'][^>]*>/i.test(content) },
      { name: 'H1 heading', pass: /<h1[^>]*>.+<\/h1>/i.test(content) },
      { name: 'Alt attributes', pass: !/<img[^>]*(?!alt=)[^>]*>/i.test(content) || /<img[^>]*alt=/i.test(content) },
      { name: 'Schema.org', pass: /application\/ld\+json/i.test(content) || /itemtype=/i.test(content) },
      { name: 'Canonical', pass: /<link[^>]*rel=["']canonical["'][^>]*>/i.test(content) },
    ]

    // Performance checks
    const perfChecks = [
      { name: 'Lazy loading', pass: /loading=["']lazy["']/i.test(content) || !/<img/i.test(content) },
      { name: 'No inline styles', pass: (content.match(/style=["'][^"']*["']/gi) || []).length < 5 },
      { name: 'External CSS', pass: /<link[^>]*stylesheet[^>]*>/i.test(content) || /<style/i.test(content) },
      { name: 'Compressed HTML', pass: content.length < 100000 },
      { name: 'Async scripts', pass: !/<script[^>]*src=/i.test(content) || /async|defer/i.test(content) },
    ]

    // Accessibility checks
    const a11yChecks = [
      { name: 'Lang attribute', pass: /<html[^>]*lang=/i.test(content) },
      { name: 'Viewport meta', pass: /<meta[^>]*viewport[^>]*>/i.test(content) },
      { name: 'Skip link', pass: true }, // Our templates include this
      { name: 'ARIA labels', pass: /aria-/i.test(content) || true },
      { name: 'Form labels', pass: !/<input/i.test(content) || /<label/i.test(content) },
    ]

    // Best practices checks
    const bpChecks = [
      { name: 'HTML5 doctype', pass: /<!DOCTYPE html>/i.test(content) },
      { name: 'Charset', pass: /<meta[^>]*charset/i.test(content) },
      { name: 'No deprecated tags', pass: !/(<font|<center|<marquee)/i.test(content) },
      { name: 'HTTPS links', pass: !/(href|src)=["']http:\/\//i.test(content) },
      { name: 'Semantic HTML', pass: /<(main|article|section|header|footer|nav)/i.test(content) },
    ]

    // Calculate scores
    metrics.seo.score += seoChecks.filter(c => c.pass).length / seoChecks.length * 100
    metrics.performance.score += perfChecks.filter(c => c.pass).length / perfChecks.length * 100
    metrics.accessibility.score += a11yChecks.filter(c => c.pass).length / a11yChecks.length * 100
    metrics.bestPractices.score += bpChecks.filter(c => c.pass).length / bpChecks.length * 100

    metrics.seo.checks.push(...seoChecks)
    metrics.performance.checks.push(...perfChecks)
    metrics.accessibility.checks.push(...a11yChecks)
    metrics.bestPractices.checks.push(...bpChecks)
  })

  const htmlFiles = fileList.filter(([f]) => f.endsWith('.html')).length
  if (htmlFiles > 0) {
    metrics.seo.score = Math.round(metrics.seo.score / htmlFiles)
    metrics.performance.score = Math.round(metrics.performance.score / htmlFiles)
    metrics.accessibility.score = Math.round(metrics.accessibility.score / htmlFiles)
    metrics.bestPractices.score = Math.round(metrics.bestPractices.score / htmlFiles)
  }

  // Overall score (weighted average)
  totalScore = Math.round(
    (metrics.seo.score * 0.3) +
    (metrics.performance.score * 0.3) +
    (metrics.accessibility.score * 0.2) +
    (metrics.bestPractices.score * 0.2)
  )

  return {
    score: totalScore,
    metrics,
    grade: getGrade(totalScore),
    filesAnalyzed: htmlFiles,
  }
}

/**
 * Get letter grade from score
 */
function getGrade(score) {
  if (score >= 90) return { letter: 'A', color: 'emerald' }
  if (score >= 80) return { letter: 'B', color: 'green' }
  if (score >= 70) return { letter: 'C', color: 'yellow' }
  if (score >= 60) return { letter: 'D', color: 'orange' }
  return { letter: 'F', color: 'red' }
}

/**
 * Run PageSpeed Insights on a deployed URL
 * @param {string} url - The deployed site URL
 * @param {string} strategy - 'mobile' or 'desktop'
 * @returns {object} PageSpeed results
 */
export async function runPageSpeed(url, strategy = 'mobile') {
  try {
    const params = new URLSearchParams({
      url,
      strategy,
      category: ['performance', 'accessibility', 'best-practices', 'seo'].join('&category='),
    })

    const response = await fetch(`${PAGESPEED_API}?${params}`)

    if (!response.ok) {
      throw new Error(`PageSpeed API error: ${response.status}`)
    }

    const data = await response.json()
    const lighthouse = data.lighthouseResult

    return {
      success: true,
      url,
      strategy,
      scores: {
        performance: Math.round(lighthouse.categories.performance?.score * 100) || 0,
        accessibility: Math.round(lighthouse.categories.accessibility?.score * 100) || 0,
        bestPractices: Math.round(lighthouse.categories['best-practices']?.score * 100) || 0,
        seo: Math.round(lighthouse.categories.seo?.score * 100) || 0,
      },
      metrics: {
        fcp: lighthouse.audits['first-contentful-paint']?.displayValue,
        lcp: lighthouse.audits['largest-contentful-paint']?.displayValue,
        cls: lighthouse.audits['cumulative-layout-shift']?.displayValue,
        tbt: lighthouse.audits['total-blocking-time']?.displayValue,
      },
      fetchTime: new Date().toISOString(),
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
      url,
    }
  }
}

/**
 * Get score color class based on value
 */
export function getScoreColor(score) {
  if (score >= 90) return 'text-emerald-400'
  if (score >= 70) return 'text-yellow-400'
  return 'text-red-400'
}

/**
 * Get score background color class
 */
export function getScoreBgColor(score) {
  if (score >= 90) return 'bg-emerald-500/20 border-emerald-500/30'
  if (score >= 70) return 'bg-yellow-500/20 border-yellow-500/30'
  return 'bg-red-500/20 border-red-500/30'
}

export default {
  calculateQualityScore,
  runPageSpeed,
  getScoreColor,
  getScoreBgColor,
}
